/**
 * Pool Reports Vessel Details Page
 *
 * Displays comprehensive vessel details including monthly info, voyage data,
 * and bunker consumption details. Matches Angular implementation exactly.
 */

import { useEffect } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { Skeleton } from '@/components/ui';
import { useVesselAllDetails } from '@/hooks/usePoolReports';
import { usePoolReportsStore } from '@/store';
import type {
  VesselDetails,
  VesselPoolPoints,
  VesselMonthlyInfo,
  VesselVoyageInfo,
  VesselVoyageNote,
  VesselBunkerInfo,
} from '@/types/poolReports.types';

export function PoolReportsVesselDetailsPage() {
  const { ownerId, vesselId } = useParams<{ ownerId: string; vesselId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { reportType, setSubTitle } = usePoolReportsStore();

  const ownerName = searchParams.get('ownerName') || 'Owner';
  const vesselName = searchParams.get('vesselName') || 'Vessel';
  const poolId = searchParams.get('poolId');
  const fileId = searchParams.get('fileId');
  const numericFileId = fileId ? parseInt(fileId, 10) : 0;
  const numericVesselId = vesselId ? parseInt(vesselId, 10) : 0;

  const {
    details: vesselDetailsData,
    poolPoints: poolPointsData,
    monthlyInfo: monthlyInfoData,
    voyageInfo: voyageInfoData,
    voyageNotes: voyageNotesData,
    bunkerInfo: bunkerInfoData,
    isLoading,
  } = useVesselAllDetails(numericFileId, numericVesselId, Boolean(fileId && vesselId));

  const vesselDetails: VesselDetails | null = vesselDetailsData || null;
  const poolPoints: VesselPoolPoints[] = poolPointsData || [];
  const monthlyInfo: VesselMonthlyInfo[] = monthlyInfoData || [];

  // TUKA-2217: the trailing summary block used to be assumed to be exactly 5 rows.
  // It is now variable - a "Total Loan" row appears only when loan expenses are non-zero -
  // so derive the boundary from the data instead. Month rows carry SectionId 1-12; every
  // summary row is 18 or above (18 totals, then 1001+).
  const summaryStartIdx = monthlyInfo.findIndex((row) => (row.sectionId ?? 0) >= 18);
  const voyageInfo: VesselVoyageInfo[] = voyageInfoData || [];
  const voyageNotes: VesselVoyageNote[] = voyageNotesData || [];
  const bunkerInfo: VesselBunkerInfo[] = bunkerInfoData || [];
  const loading = isLoading;

  // Set subtitle for navigation (hide pool/report filters, show back button)
  useEffect(() => {
    const pathSegments = location.pathname.split('/');
    const currentTab = pathSegments[3];

    setSubTitle({
      value: `Vessel Details: ${ownerName} - ${vesselName}`,
      backLink: `/reports/${reportType}/${currentTab}/owner/${ownerId}`,
      queryParams: {
        poolId: poolId || '',
        fileId: fileId || '',
        ownerName: ownerName,
      },
    });

    return () => {
      setSubTitle(null);
    };
  }, [ownerId, ownerName, vesselName, reportType, poolId, fileId, location.pathname, setSubTitle]);

  // Format date helper
  const formatDate = (dateString: string | null, format: 'short' | 'month' = 'short'): string => {
    if (!dateString) return '--';
    const date = new Date(dateString);
    if (format === 'month') {
      return date.toLocaleDateString('en-US', { month: '2-digit', year: 'numeric' });
    }
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Format number with thousand separators
  const formatNumber = (
    value: number | string | null | undefined,
    minDec = 0,
    maxDec = 0
  ): string => {
    if (value === null || value === undefined || value === '') return '';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: minDec,
      maximumFractionDigits: maxDec,
    });
  };

  // Common table cell classes
  const thClass =
    'border-b-2 border-gray-200 bg-gray-50 px-2.5 py-1.5 text-right text-[10px] font-bold uppercase leading-tight text-gray-700 dark:border-dark-700 dark:bg-dark-800 dark:text-gray-300 sm:px-3 sm:py-2 sm:text-xs';
  const thFirstClass =
    'border-b-2 border-r border-gray-200 bg-gray-50 px-2.5 py-1.5 text-right text-[10px] font-bold uppercase leading-tight text-gray-700 dark:border-dark-700 dark:bg-dark-800 dark:text-gray-300 sm:px-3 sm:py-2 sm:text-xs sticky left-0 z-20 min-w-[140px]';
  const tdClass =
    'whitespace-nowrap border-b border-gray-200 bg-white px-2.5 py-2 text-right text-sm text-gray-900 dark:border-dark-700 dark:bg-dark-900 dark:text-gray-300 sm:px-3 sm:py-2.5';
  const tdFirstClass =
    'whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-2 text-right text-sm font-semibold uppercase text-gray-900 dark:border-dark-700 dark:text-gray-200 sm:px-3 sm:py-2.5 sticky left-0 z-20 min-w-[140px] bg-white dark:bg-dark-900';

  return (
    <div className="vessel-details-page mx-auto space-y-5">
      {/* Vessel Basic Details */}
      <div className="details-container rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-dark-700 dark:bg-dark-900">
        <div className="vessel-details grid grid-cols-1 gap-2.5 md:grid-cols-2">
          <div className="item flex">
            <span className="label min-w-[130px] text-sm opacity-75 dark:text-gray-400">
              MONTH BUILT
            </span>
            <span className="value font-semibold text-gray-900 dark:text-white">
              {loading ? (
                <Skeleton className="h-4 w-24" />
              ) : (
                formatDate(vesselDetails?.dateBuilt || null, 'month')
              )}
            </span>
          </div>
          <div className="item flex">
            <span className="label min-w-[130px] text-sm opacity-75 dark:text-gray-400">
              SUMMER DWT
            </span>
            <span className="value font-semibold text-gray-900 dark:text-white">
              {loading ? <Skeleton className="h-4 w-24" /> : vesselDetails?.dwt || '--'}
            </span>
          </div>
          <div className="item flex">
            <span className="label min-w-[130px] text-sm opacity-75 dark:text-gray-400">
              YARD BUILT
            </span>
            <span className="value font-semibold text-gray-900 dark:text-white">
              {loading ? <Skeleton className="h-4 w-24" /> : vesselDetails?.yardBuilt || '--'}
            </span>
          </div>
          <div className="item flex">
            <span className="label min-w-[130px] text-sm opacity-75 dark:text-gray-400">
              ENGINE MAKER
            </span>
            <span className="value font-semibold text-gray-900 dark:text-white">
              {loading ? <Skeleton className="h-4 w-24" /> : vesselDetails?.engineMaker || '--'}
            </span>
          </div>
          <div className="item flex">
            <span className="label min-w-[130px] text-sm opacity-75 dark:text-gray-400">
              ENGINE TYPE
            </span>
            <span className="value font-semibold text-gray-900 dark:text-white">
              {loading ? <Skeleton className="h-4 w-24" /> : vesselDetails?.engineType || '--'}
            </span>
          </div>
          <div className="item flex">
            <span className="label min-w-[130px] text-sm opacity-75 dark:text-gray-400">
              POOL POINTS
            </span>
            <div className="value font-semibold text-gray-900 dark:text-white">
              {loading ? (
                <>
                  <Skeleton className="mb-2 h-4 w-32" />
                  <Skeleton className="h-4 w-32" />
                </>
              ) : (
                <>
                  {poolPoints.map((item, idx) => (
                    <div key={idx}>
                      {formatDate(item.startDate)} - {formatDate(item.endDate)} ({item.poolPoints})
                    </div>
                  ))}
                  {!poolPoints.length && <div>--</div>}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Information Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-900">
        <div className="report-table-shell">
          <table className="report-table">
            <thead>
              <tr>
                <th className={thFirstClass} style={{ minWidth: '200px' }}>
                  MONTH
                </th>
                <th className={thClass}>ONHIRE DAYS</th>
                <th className={thClass}>
                  NET
                  <br />
                  CONTRIBUTION TOTAL
                </th>
                <th className={thClass}>
                  POOL
                  <br />
                  ALLOCATION TOTAL
                </th>
                <th className={thClass}>
                  AVG
                  <br />
                  POOL POINTS
                </th>
                <th className={thClass}>
                  ALLOCATION
                  <br />
                  RPD
                </th>
                <th className={`${thClass} !text-left`} style={{ minWidth: '200px' }}>
                  DESCRIPTION
                </th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 17 }).map((_, idx) => (
                    <tr key={idx}>
                      {Array.from({ length: 7 }).map((_, colIdx) => (
                        <td
                          key={colIdx}
                          className={colIdx === 0 ? tdFirstClass : tdClass}
                          style={colIdx === 0 ? { minWidth: '200px' } : undefined}
                        >
                          <Skeleton className="h-6 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : monthlyInfo.map((item, idx) => {
                    const isSummaryRow = summaryStartIdx !== -1 && idx >= summaryStartIdx;
                    const isSeparatorRow = summaryStartIdx > 0 && idx === summaryStartIdx - 1;

                    // Summary row styling (the trailing totals block)
                    const summaryBg = isSummaryRow ? 'bg-gray-100 dark:bg-dark-800' : '';
                    const summaryFont = isSummaryRow ? 'font-semibold' : '';
                    const separatorBorder = isSeparatorRow ? 'border-b-2' : '';

                    return (
                      <tr key={idx} className={summaryBg}>
                        <td
                          className={`${tdFirstClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                          style={{ minWidth: '200px' }}
                        >
                          {item.month}
                        </td>
                        <td
                          className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                        >
                          {formatNumber(item.onHireDays, 2, 2)}
                        </td>
                        <td
                          className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                        >
                          {formatNumber(item.netContributionTotal, 0, 0)}
                        </td>
                        <td
                          className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                        >
                          {formatNumber(item.poolAllocationTotal, 0, 0)}
                        </td>
                        <td
                          className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                        >
                          {formatNumber(item.averagePoolPoints, 0, 0)}
                        </td>
                        <td
                          className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                        >
                          {formatNumber(item.allocationRPD, 0, 0)}
                        </td>
                        <td
                          className={`${tdClass} !text-left ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                        >
                          {item.description}
                        </td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Monthly More Info Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-900">
        <div className="report-table-shell">
          <table className="report-table">
            <thead>
              <tr>
                <th className={thFirstClass} style={{ minWidth: '200px' }}>
                  MONTH
                </th>
                <th className={thClass}>CONSUMPTION HSFO MT</th>
                <th className={thClass}>CONSUMPTION LSFO MT</th>
                <th className={thClass}>LIFTING HFO MT</th>
                <th className={thClass}>MONTH END CALCULATED HFO MT</th>
                <th className={thClass}>MONTH END ROB TOTAL COST</th>
                <th className={thClass}>MONTH END ACCRUED FREIGHT</th>
                <th className={thClass}>MONTH END WORKING CAPITAL</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 13 }).map((_, idx) => (
                    <tr key={idx}>
                      {Array.from({ length: 8 }).map((_, colIdx) => (
                        <td
                          key={colIdx}
                          className={colIdx === 0 ? tdFirstClass : tdClass}
                          style={colIdx === 0 ? { minWidth: '200px' } : undefined}
                        >
                          <Skeleton className="h-6 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : (() => {
                    const moreInfoData = monthlyInfo.slice(0, 13);
                    return moreInfoData.map((item, idx) => {
                      const posFromEnd = moreInfoData.length - idx;
                      const isSummaryRow = posFromEnd === 1; // Last row only
                      const isSeparatorRow = posFromEnd === 2; // 2nd from last

                      const summaryBg = isSummaryRow ? 'bg-gray-100 dark:bg-dark-800' : '';
                      const summaryFont = isSummaryRow ? 'font-semibold' : '';
                      const separatorBorder = isSeparatorRow ? 'border-b-2' : '';

                      return (
                        <tr key={idx} className={summaryBg}>
                          <td
                            className={`${tdFirstClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                            style={{ minWidth: '200px' }}
                          >
                            {item.month}
                          </td>
                          <td
                            className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                          >
                            {formatNumber(item.consumptionHSFOMT, 2, 2)}
                          </td>
                          <td
                            className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                          >
                            {formatNumber(item.consumptionLSFOMT, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                          >
                            {formatNumber(item.liftingHFOMT, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                          >
                            {formatNumber(item.monthEndcalculatedHFOMT, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                          >
                            {formatNumber(item.monthEndROBTotalCost, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                          >
                            {formatNumber(item.monthEndAccruedFreight, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} ${summaryFont} ${separatorBorder} ${isSummaryRow ? '!bg-gray-100 dark:!bg-dark-800' : ''}`}
                          >
                            {formatNumber(item.monthEndWorkingCapital, 0, 0)}
                          </td>
                        </tr>
                      );
                    });
                  })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* Voyage Info Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-900">
        <div className="report-table-shell">
          <table className="report-table">
            <thead>
              <tr>
                <th className={thFirstClass}>VOYAGE NO</th>
                <th className={thClass}>START DATE</th>
                <th className={thClass}>END DATE</th>
                <th className={thClass}>ONHIRE DAYS</th>
                <th className={thClass}>OFF DAYS</th>
                <th className={thClass}>IDLE DAYS</th>
                <th className={thClass}>VOYAGE TYPE</th>
                <th className={thClass}>LOAD AREA</th>
                <th className={thClass}>DISCHARGE AREA</th>
                <th className={thClass}>VOYAGE CHARTERER</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 10 }).map((_, idx) => (
                    <tr key={idx}>
                      {Array.from({ length: 10 }).map((_, colIdx) => (
                        <td key={colIdx} className={colIdx === 0 ? tdFirstClass : tdClass}>
                          <Skeleton className="h-6 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : voyageInfo.slice(0, -2).map((item, idx) => (
                    <tr
                      key={idx}
                      className={item.isBasisYear === false ? 'bg-amber-100 dark:bg-amber-950' : ''}
                    >
                      <td
                        className={`${tdFirstClass} ${item.isBasisYear === false ? '!bg-amber-100 dark:!bg-amber-950' : ''}`}
                      >
                        {item.voyageNo}
                      </td>
                      <td className={tdClass}>{formatDate(item.startDate)}</td>
                      <td className={tdClass}>{formatDate(item.endDate)}</td>
                      <td className={tdClass}>{formatNumber(item.onhireDays, 2, 4)}</td>
                      <td className={tdClass}>{formatNumber(item.offDays, 2, 2)}</td>
                      <td className={tdClass}>{formatNumber(item.idleDays, 2, 2)}</td>
                      <td className={tdClass}>{item.voyageType}</td>
                      <td className={tdClass}>{item.loadArea}</td>
                      <td className={tdClass}>{item.dischargeArea}</td>
                      <td className={tdClass}>{item.voyageCharterer}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Voyage Costing Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-900">
        <div className="report-table-shell">
          <table className="report-table">
            <thead>
              <tr>
                <th className={thFirstClass}>VOYAGE NO</th>
                <th className={thClass}>START DATE</th>
                <th className={thClass}>END DATE</th>
                <th className={thClass}>GROSS FREIGHT</th>
                <th className={thClass}>BUNKERS</th>
                <th className={thClass}>OTHER COSTS</th>
                <th className={thClass}>VOYAGE CONTRIBUTION</th>
                <th className={thClass}>NET ALLOCATION</th>
                <th className={thClass}>CONTRIBUTION RPD</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 17 }).map((_, idx) => (
                    <tr key={idx}>
                      {Array.from({ length: 9 }).map((_, colIdx) => (
                        <td key={colIdx} className={colIdx === 0 ? tdFirstClass : tdClass}>
                          <Skeleton className="h-6 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : voyageInfo.map((item, idx) => {
                    const isSummaryRow = idx >= voyageInfo.length - 3;
                    const isFirstSummaryRow = idx === voyageInfo.length - 3;

                    if (isSummaryRow) {
                      return (
                        <tr key={idx} className="bg-gray-100 font-semibold dark:bg-dark-800">
                          <td
                            colSpan={3}
                            className={`sticky left-0 z-20 min-w-[140px] whitespace-nowrap border-b border-r border-gray-200 bg-gray-100 px-2.5 py-2 text-right text-sm font-semibold uppercase text-gray-900 dark:border-dark-700 dark:bg-dark-800 dark:text-gray-200 sm:px-3 sm:py-2.5 ${isFirstSummaryRow ? 'border-t-2' : ''}`}
                          >
                            {item.voyageNo}
                          </td>
                          <td
                            className={`${tdClass} !bg-gray-100 font-semibold dark:!bg-dark-800 dark:!text-gray-200 ${isFirstSummaryRow ? 'border-t-2' : ''}`}
                          >
                            {formatNumber(item.grossFreight, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} !bg-gray-100 font-semibold dark:!bg-dark-800 dark:!text-gray-200 ${isFirstSummaryRow ? 'border-t-2' : ''}`}
                          >
                            {formatNumber(item.bunkers, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} !bg-gray-100 font-semibold dark:!bg-dark-800 dark:!text-gray-200 ${isFirstSummaryRow ? 'border-t-2' : ''}`}
                          >
                            {formatNumber(item.otherCosts, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} !bg-gray-100 font-semibold dark:!bg-dark-800 dark:!text-gray-200 ${isFirstSummaryRow ? 'border-t-2' : ''}`}
                          >
                            {formatNumber(item.voyageContribution, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} !bg-gray-100 font-semibold dark:!bg-dark-800 dark:!text-gray-200 ${isFirstSummaryRow ? 'border-t-2' : ''}`}
                          >
                            {formatNumber(item.netAllocation, 0, 0)}
                          </td>
                          <td
                            className={`${tdClass} !bg-gray-100 font-semibold dark:!bg-dark-800 dark:!text-gray-200 ${isFirstSummaryRow ? 'border-t-2' : ''}`}
                          >
                            {formatNumber(item.contributionRPD, 0, 0)}
                          </td>
                        </tr>
                      );
                    }

                    return (
                      <tr
                        key={idx}
                        className={
                          item.isBasisYear === false ? 'bg-amber-100 dark:bg-amber-950' : ''
                        }
                      >
                        <td
                          className={`${tdFirstClass} ${item.isBasisYear === false ? '!bg-amber-100 dark:!bg-amber-950' : ''}`}
                        >
                          {item.voyageNo}
                        </td>
                        <td className={tdClass}>{formatDate(item.startDate)}</td>
                        <td className={tdClass}>{formatDate(item.endDate)}</td>
                        <td className={tdClass}>{formatNumber(item.grossFreight, 0, 0)}</td>
                        <td className={tdClass}>{formatNumber(item.bunkers, 0, 0)}</td>
                        <td className={tdClass}>{formatNumber(item.otherCosts, 0, 0)}</td>
                        <td className={tdClass}>{formatNumber(item.voyageContribution, 0, 0)}</td>
                        <td className={tdClass}>{formatNumber(item.netAllocation, 0, 0)}</td>
                        <td className={tdClass}>{formatNumber(item.contributionRPD, 0, 0)}</td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </div>

      {/* MORE DETAILS - BUNKERS Section Heading */}
      <h2 className="mb-2 mt-6 text-xl font-bold text-gray-900 dark:text-white">
        MORE DETAILS - BUNKERS: DETAILED VOYAGE CONSUMPTIONS
      </h2>

      {/* Bunker Info Table - Steaming Days */}
      <div className="mt-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-900">
        <div className="report-table-shell">
          <table className="report-table">
            <thead>
              <tr>
                <th className={thFirstClass}>VOYAGE NO</th>
                <th className={thClass}>START DATE</th>
                <th className={thClass}>END DATE</th>
                <th className={thClass}>DAY STEAMING BALLAST</th>
                <th className={thClass}>DAY STEAMING LADEN</th>
                <th className={thClass}>BALLAST DISTANCE EST NM</th>
                <th className={thClass}>LADEN DISTANCE EST NM</th>
                <th className={thClass}>
                  NET ONHIRE
                  <br />
                  DAYS
                </th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 10 }).map((_, idx) => (
                    <tr key={idx}>
                      {Array.from({ length: 8 }).map((_, colIdx) => (
                        <td key={colIdx} className={colIdx === 0 ? tdFirstClass : tdClass}>
                          <Skeleton className="h-6 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : bunkerInfo.map((item, idx) => (
                    <tr
                      key={idx}
                      className={item.isAccrual ? 'bg-amber-100 dark:bg-amber-950' : ''}
                    >
                      <td
                        className={`${tdFirstClass} ${item.isAccrual ? '!bg-amber-100 dark:!bg-amber-950' : ''}`}
                      >
                        {item.voyageNo}
                      </td>
                      <td className={tdClass}>
                        {!item.isAccrual ? formatDate(item.startDate) : ''}
                      </td>
                      <td className={tdClass}>
                        {item.isAccrual ? 'Accrual' : formatDate(item.endDate)}
                      </td>
                      <td className={tdClass}>{formatNumber(item.daysSteamingBallast, 2, 2)}</td>
                      <td className={tdClass}>{formatNumber(item.daysSteamingLaden, 2, 2)}</td>
                      <td className={tdClass}>
                        {formatNumber(item.ballastDistanceEstimateNM, 0, 0)}
                      </td>
                      <td className={tdClass}>
                        {formatNumber(item.ladenDistanceEstimateNM, 0, 0)}
                      </td>
                      <td className={tdClass}>{formatNumber(item.netOnhireDays, 2, 2)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bunker Consumption Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-900">
        <div className="report-table-shell">
          <table className="report-table">
            <thead>
              <tr>
                <th className={thFirstClass}>VOYAGE NO</th>
                <th className={thClass}>START DATE</th>
                <th className={thClass}>END DATE</th>
                <th className={thClass}>TOTAL HSFO CONSUMED MT</th>
                <th className={thClass}>TOTAL LSFO CONSUMED MT</th>
                <th className={thClass}>AVG HFO CONSUMED MT/DAY</th>
                <th className={thClass}>AVG UNIT COST HFO $/MT</th>
                <th className={thClass}>TOTAL MDO & OTHER COSTS</th>
                <th className={thClass}>
                  TOTAL COSTS
                  <br />
                  BUNKER $
                </th>
                <th className={thClass}>
                  BUNKER
                  <br />
                  COSTS $/DAY
                </th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? Array.from({ length: 10 }).map((_, idx) => (
                    <tr key={idx}>
                      {Array.from({ length: 10 }).map((_, colIdx) => (
                        <td key={colIdx} className={colIdx === 0 ? tdFirstClass : tdClass}>
                          <Skeleton className="h-6 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : bunkerInfo.slice(0, -1).map((item, idx) => (
                    <tr
                      key={idx}
                      className={item.isAccrual ? 'bg-amber-100 dark:bg-amber-950' : ''}
                    >
                      <td
                        className={`${tdFirstClass} ${item.isAccrual ? '!bg-amber-100 dark:!bg-amber-950' : ''}`}
                      >
                        {item.voyageNo}
                      </td>
                      <td className={tdClass}>
                        {!item.isAccrual ? formatDate(item.startDate) : ''}
                      </td>
                      <td className={tdClass}>
                        {item.isAccrual ? 'Accrual' : formatDate(item.endDate)}
                      </td>
                      <td className={tdClass}>{formatNumber(item.totalHSFOConsumedMT, 0, 0)}</td>
                      <td className={tdClass}>{formatNumber(item.totalLSFOConsumedMT, 0, 0)}</td>
                      <td className={tdClass}>
                        {formatNumber(item.averageHFOConsumedMTPerDay, 2, 2)}
                      </td>
                      <td className={tdClass}>
                        {formatNumber(item.averageUnitCostHFOPricePerMT, 0, 0)}
                      </td>
                      <td className={tdClass}>{formatNumber(item.totalMDOandOtherCosts, 0, 0)}</td>
                      <td className={tdClass}>{formatNumber(item.totalBunkerCosts, 0, 0)}</td>
                      <td className={tdClass}>{formatNumber(item.bunkerCostsPerDay, 0, 0)}</td>
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Voyage Notes - At the bottom */}
      {voyageNotes.length > 0 && (
        <div className="voyage-notes overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-900">
          {/* Header Row */}
          <div className="flex border-b-2 border-gray-200 font-bold text-gray-900 dark:border-dark-600 dark:text-white">
            <div className="voyage-no-column relative flex min-w-[120px] shrink-0 items-center justify-end border-r-2 border-gray-400 px-4 py-2 dark:border-gray-500 sm:min-w-[150px]">
              VOYAGE NO
            </div>
            <div className="flex-1 px-4 py-2">NOTES</div>
          </div>
          {/* Notes Items */}
          {voyageNotes.map((note, idx) => (
            <div key={idx} className="flex">
              <div className="voyage-no-column relative flex min-w-[120px] shrink-0 items-center justify-end border-r-2 border-gray-400 px-4 py-2 font-bold text-gray-900 dark:border-gray-500 dark:text-white sm:min-w-[150px]">
                #{note.voyageNo}
                {/* Blue dot on the divider line */}
                <span
                  className="absolute -right-[5px] top-1/2 h-2 w-2 -translate-y-1/2 rounded-full border-2 border-white bg-primary-500 dark:border-dark-900"
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1 break-words px-4 py-2 text-gray-600 dark:text-gray-200">
                {note.voyageUserNotes}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
